import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';

import { Subject, takeUntil } from 'rxjs';

import { I18nService, TranslatePipe } from '@recruitee/i18n';
import {
  AddNewButtonComponent,
  DialogService,
  DialogWidth,
  DividerComponent,
  IconComponent,
} from '@recruitee/user-interface';

import { HttpErrorHandlerService } from '../../common';
import { DynamicHelpers } from '../../dynamic/helpers';
import { DynamicText } from '../../dynamic/types';
import { FieldMappingStoreService } from '../data/src/field-mapping/field-mapping.service';
import { FieldMappingFormFieldMappingComponent } from '../field-mapping-form-field-mapping/field-mapping-form-field-mapping.component';
import { FieldMapping, FormInput, Source } from '../types';

@Component({
  selector: 'rti-field-mapping-form-field',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './field-mapping-form-field.component.html',
  styleUrls: ['./field-mapping-form-field.component.less'],
  standalone: true,
  imports: [
    AddNewButtonComponent,
    DividerComponent,
    DragDropModule,
    FieldMappingFormFieldMappingComponent,
    IconComponent,
    TranslatePipe,
  ],
})
export class FieldMappingFormFieldComponent {
  @Input() public formInput: FormInput;
  @Input() public integrationName: string;
  @Input() public integrationKind: string;
  @Input() public sourcesLoading: boolean = true;
  @Input() public sources: Source[];

  @Output() public formFieldChanged: EventEmitter<FormInput> = new EventEmitter();

  public editable: boolean = false;
  public label: string;
  public ifElseLabel: string;

  @Input() private formConfigId: number;

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    protected dialogService: DialogService,
    private helpers: DynamicHelpers,
    private cdRef: ChangeDetectorRef,
    private i18n: I18nService,
    private fieldMappingStore: FieldMappingStoreService,
    private httpErrorHandlerService: HttpErrorHandlerService,
  ) {}

  public ngOnInit(): void {
    this.ifElseLabel = this.getIfElseLabel();
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public toggleEditable(): void {
    this.editable = !this.editable;

    this.cdRef.detectChanges();
  }

  public onDrop(event: CdkDragDrop<FieldMapping>): void {
    const previousOrder = [...this.formInput.fieldMappings];

    moveItemInArray(this.formInput.fieldMappings, event.previousIndex, event.currentIndex);

    this.cdRef.detectChanges();

    const orderedIds = this.formInput.fieldMappings.map(fm => fm.id);

    this.fieldMappingStore
      .updateFieldMappingsPositions(this.integrationKind, this.formConfigId, {
        orderedIds,
        inputChecksum: this.formInput.checksum,
      })
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe({
        error: error => {
          this.httpErrorHandlerService.default(error);

          this.formInput.fieldMappings = previousOrder;

          this.cdRef.detectChanges();
        },
      });
  }

  public async openDialog(): Promise<void> {
    const { FieldMappingAddFieldMappingDialogComponent } = await import(
      '../field-mapping-add-field-mapping-dialog/field-mapping-add-field-mapping-dialog.component'
    );

    this.dialogService
      .openCustom(
        FieldMappingAddFieldMappingDialogComponent,
        { width: DialogWidth.SmallX },
        {
          title: this.getFieldName(),
          subtitle: this.i18n.translate('rti.field_mapping.settings.add.dialog.subtitle', {
            integrationName: this.integrationName,
          }),
          isLoading: this.sourcesLoading,
          integrationKind: this.integrationKind,
          formConfigId: this.formConfigId,
          formInput: this.formInput,
          sources: this.sources,
        },
      )
      .afterClosed.pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(fieldMappings => {
        if (fieldMappings) {
          this.formInput.fieldMappings = [...this.formInput.fieldMappings, ...fieldMappings];

          this.formFieldChanged.emit(this.formInput);

          this.cdRef.detectChanges();
        }
      });
  }

  public handleFieldMappingDeletion(id: number): void {
    this.formInput.fieldMappings = this.formInput.fieldMappings.filter(fm => fm.id !== id);

    this.formFieldChanged.emit(this.formInput);

    this.cdRef.detectChanges();
  }

  public trackById(index: number, { id }: FieldMapping): number {
    return id;
  }

  public trackByIndex(index: number): number {
    return index;
  }

  public showIfElseLabel(itemsCount: number, isFirst: boolean): boolean {
    return itemsCount > 1 && !isFirst;
  }

  public buildText(label: DynamicText): string {
    return this.helpers.buildText(label);
  }

  private getFieldName(): string {
    if (!this.formInput.label.length) return '';

    return this.helpers.buildText(this.formInput.label.slice(-1)[0]);
  }

  private getIfElseLabel(): string {
    return this.i18n.translate('rti.field_mapping.settings.if_else');
  }
}
